
import React, { createContext, useContext, useState } from "react";
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from "react-error-boundary";
import { ErrorBoundary } from "@/components/admin/ErrorBoundary";

type ErrorBoundaryContextType = {
  captureError: (error: Error, info: { componentStack: string }) => void;
  lastError: Error | null;
  resetError: () => void;
};

const ErrorBoundaryContext = createContext<ErrorBoundaryContextType | undefined>(undefined);

// Create a fallback component that adapts our ErrorBoundary to the FallbackProps interface
const FallbackComponent = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <div className="min-h-[400px] flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {error && (
          <div className="bg-destructive/15 p-4 rounded-md border border-destructive text-destructive">
            <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
            <p className="text-sm mb-4">{error.message || 'An unexpected error occurred'}</p>
            <div className="flex gap-3 mt-4">
              <button 
                className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90 transition-colors"
                onClick={resetErrorBoundary}
              >
                Try Again
              </button>
              <button 
                className="bg-secondary text-secondary-foreground px-4 py-2 rounded hover:bg-secondary/90 transition-colors"
                onClick={() => window.location.href = '/'}
              >
                Go Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ErrorBoundaryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lastError, setLastError] = useState<Error | null>(null);

  const captureError = (error: Error, info: { componentStack: string }) => {
    console.error("Error caught by boundary context:", error, info);
    setLastError(error);
  };

  const resetError = () => {
    setLastError(null);
  };

  return (
    <ErrorBoundaryContext.Provider value={{ captureError, lastError, resetError }}>
      <ReactErrorBoundary FallbackComponent={FallbackComponent} onError={captureError}>
        {children}
      </ReactErrorBoundary>
    </ErrorBoundaryContext.Provider>
  );
};

export const useErrorBoundary = () => {
  const context = useContext(ErrorBoundaryContext);
  if (context === undefined) {
    throw new Error("useErrorBoundary must be used within an ErrorBoundaryProvider");
  }
  return context;
};
